/**
 * 지금 이 화면이 **내부용인가 외부용인가**를 정하는 자리.
 *
 * 두 얼굴이 무엇이고 왜 나눴는지는 shared/external-edition.ts 의 머리말이 진본이다. 여기는
 * "지금 누가 보고 있고, 어느 얼굴을 보여줄 것인가" 한 가지만 정한다.
 *
 * ## 진본은 주소(URL)다 — `?edition=external`
 * 브라우저 기억(localStorage)에 두지 않고 **주소에 싣는다.** 이 프로젝트의 공통 규칙
 * ("화면 상태는 URL이 진본" — ops/workflow/frontend-conventions.md)을 따르는 것이고,
 * 실질적인 이유가 셋이다.
 *   ① **스크린챗이 화면을 알아본다.** 티켓은 "이 화면"을 가리키는데, 내부용과 외부용이
 *      주소를 공유하면 어느 얼굴에서 찍힌 요청인지 구분할 방법이 없다. 주소에 실려 있으면
 *      링크만 보고 갈린다 — 그래서 외부용은 **누가 보든** 주소에 이 표가 붙는다.
 *   ② **링크로 보낼 수 있다.** "외부용에선 이렇게 보여요"를 주소 하나로 전달한다.
 *   ③ **새로고침·뒤로가기가 말이 된다.** 기억에만 있으면 뒤로 가도 얼굴이 안 바뀐다.
 *
 * ## 누가 무엇을 보나 — 선은 **두 개**다
 * ① **전환할 수 있는 사람** — 라인업 매장(admin-demo) 대표 계정(lineup)과 본사(전역) 관리자.
 *    두 얼굴을 오가며 미리보기 할 수 있다(주소가 시키는 대로 그린다). 판정식은
 *    StoreLeadRoute·InboundMessagesRoute 가 쓰는 것과 같다 — 라인업 전용 화면들이 이미 같은
 *    선으로 갈려 있어서, 선을 새로 발명하면 두 기준이 어긋난다.
 * ② **외부 배포판 대상** — **서버가 그렇게 판정한 계정**(profile.edition). 이들은 주소에
 *    무엇이 적혀 있든 외부용만 본다 — 주소에서 표를 지워 내부 화면을 여는 길이 없어야 하기
 *    때문이다. 전환 손잡이도 안 보인다.
 *
 * 둘 다 아닌 계정, 즉 **기존부터 쓰던 판매점들은 예전 그대로 전체 화면**을 본다.
 * 이 셋째 갈래가 이 파일에서 제일 중요하다 — 처음엔 없어서, 배포하는 순간 실사용 매장들의
 * 화면이 통째로 사라졌다(2026-08-19. 경위는 shared/external-edition.ts 의 기준선 주석이 진본).
 *
 * ## 판정은 화면이 하지 않는다 (2026-08-22)
 * 예전엔 이 파일이 가입 시각을 보고 직접 판정했다. 지금은 **서버가 매장 칸을 보고 판정해
 * 내려준다**(`profile.edition`). 화면은 매장 칸을 알 방법이 없고, 무엇보다 같은 판정을 두
 * 곳에서 따로 하면 서버 잠금과 화면 울타리가 어긋나는 날이 온다.
 *
 * ## 이건 계정 전환이 아니다
 * 내부 사람이 외부용으로 넘어가도 신분·권한은 그대로고 서버에 아무것도 알리지 않는다.
 * **우리가 파는 물건이 어떻게 보이는지 그 자리에서 확인하는 미리보기**다.
 */

import { createContext, useContext, useMemo } from "react";
import { useLocation } from "react-router";
import { useAuth } from "./AuthContext";
import type { Edition } from "../../shared/external-edition";

export type { Edition };

/** 주소에 붙는 표. 전체 이용 매장 화면은 아무것도 안 붙인다(기존 링크·북마크가 그대로 산다). */
export const EDITION_PARAM = "edition";
/** 미리보기 주소에 붙는 값. 옛 값(trial·external)도 계속 받는다(북마크 보호). */
export const EDITION_PARTIAL_VALUE = "partial";
const EDITION_PARTIAL_LEGACY_VALUES = ["trial", "external"];

interface EditionState {
  /** 지금 그려야 할 얼굴. */
  edition: Edition;
  /** 이 사람이 두 화면을 오갈 수 있나(=라인업·본사 관리자인가). */
  canSwitch: boolean;
  /**
   * 선택 이용 매장에게 **지금 켜져 있는** 기능 key 들(관리자가 /admin/external-features 에서 정한다).
   *
   * 메뉴·주소 통과·착지점이 전부 이 목록 하나를 본다 — 서로 어긋날 수가 없다.
   * 프로필과 같은 응답에 실려 오므로 `ready` 가 켜지는 순간 이 값도 이미 도착해 있다.
   */
  enabledFeatures: string[];
  /**
   * 신분이 확정됐나(프로필 도착). **false 인 동안 주소를 고쳐선 안 된다.**
   *
   * 🕳️ 여기가 함정이었다: 프로필이 오기 전엔 누구든 "전환 못 하는 사람"으로 보이므로 체험으로
   *    판정된다. 그 순간 가드가 주소에 `?edition=trial` 을 박아 버리면, 프로필이 도착해
   *    라인업이라는 게 밝혀져도 **주소가 이미 체험이라 그대로 굳는다.** 라인업 계정이
   *    정식 화면을 새로고침할 때마다 체험으로 튕기는 셈이다. 그래서 가드는 이 값이 켜진
   *    뒤에만 주소를 손댄다.
   */
  ready: boolean;
}

// 읽기 전용이다 — **보는 화면을 바꾸는 건 주소를 바꾸는 일**이므로 여기에 setter 를 두지 않는다.
// 바꾸려면 아래 withExternalSearch/withoutExternalSearch 로 주소를 만들어 navigate 한다
// (헤더의 [내부용|외부용] 탭이 그 유일한 손잡이였으나 2026-08-28 에 뺐다 — 지금은 주소에
// `?edition=trial` 을 직접 붙여 미리보기한다). setter 를 두면 "상태를 바꾸는 길"이 둘이 되어 주소와 어긋나기 시작한다.
const EditionContext = createContext<EditionState>({
  edition: "partial",
  canSwitch: false,
  enabledFeatures: [],
  ready: false,
});

/** 이 주소가 선택 이용 매장 화면을 가리키나. 가드·링크 만들기가 함께 쓴다. */
export function isExternalSearch(search: string): boolean {
  const v = new URLSearchParams(search).get(EDITION_PARAM);
  return v === EDITION_PARTIAL_VALUE || (v != null && EDITION_PARTIAL_LEGACY_VALUES.includes(v));
}

/** 주소에 체험 미리보기 표를 붙인 검색문자열을 만든다(이미 있으면 그대로). */
export function withExternalSearch(search: string): string {
  const params = new URLSearchParams(search);
  params.set(EDITION_PARAM, EDITION_PARTIAL_VALUE);
  return `?${params.toString()}`;
}

/** 주소에서 체험 미리보기 표만 뗀다(다른 파라미터는 건드리지 않는다). */
export function withoutExternalSearch(search: string): string {
  const params = new URLSearchParams(search);
  params.delete(EDITION_PARAM);
  const s = params.toString();
  return s ? `?${s}` : "";
}

export function EditionProvider({ children }: { children: React.ReactNode }) {
  const { profile, hasHqDelegation } = useAuth();
  const location = useLocation();

  const ready = profile != null;
  // 체험 화면을 미리 볼 수 있는 사람 = 그 내용을 정하는 사람과 같은 선(백엔드
  // canManageExternalEdition 과 동일). 2026-08-28 부터 라인업 매장 번호가 아니라
  // 본사 위임 권한(hq_shared)으로 판정한다.
  const canSwitch = ready && hasHqDelegation();
  // 선택 이용 매장인가 — **서버 판정을 그대로 따른다.** 옛 서버가 값을 안 내려주면
  // 정식으로 친다(쓰던 매장의 화면이 사라지는 게 제일 나쁜 실패라 안전한 쪽으로 기운다).
  const isExternalAccount = ready && profile.edition === "partial";
  const enabledFeatures = useMemo(
    () => (ready ? (profile.externalFeatures ?? []) : []),
    [ready, profile?.externalFeatures],
  );

  // 셋으로 갈린다.
  //   ① 선택 이용 매장 계정 → **주소와 무관하게** 체험 화면. 주소에서 표를 지워 정식 화면을
  //      여는 길을 막는다.
  //   ② 전환할 수 있는 사람(라인업·본사 관리자) → 주소가 시키는 대로(미리보기).
  //   ③ 그 외 = **전체 이용 매장 매장** → 언제나 전 기능. 표가 붙은 링크를 눌러도 무시한다 —
  //      전환 손잡이가 없는 사람이 체험 화면에 들어가면 되돌아올 길이 없고, 그 순간 그 매장은
  //      업무 화면을 잃는다. 링크 한 번에 그런 일이 생겨선 안 된다.
  // 신분 확정 전(ready=false)에는 ②·③ 어느 쪽인지 모르므로 정식으로 그린다. 가드도 그동안
  // 주소를 안 건드리므로(ready 주석 참조), 확정된 뒤 제자리를 찾는다.
  const marked = isExternalSearch(location.search);
  const edition: Edition = isExternalAccount ? "partial" : canSwitch && marked ? "partial" : "full";

  const value = useMemo(
    () => ({ edition, canSwitch, enabledFeatures, ready }),
    [edition, canSwitch, enabledFeatures, ready],
  );

  return <EditionContext.Provider value={value}>{children}</EditionContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useEdition(): EditionState {
  return useContext(EditionContext);
}
